import { ExternalLinkIcon } from "@heroicons/react/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/solid";
import { Search } from "lucide-react";
import { observer } from "mobx-react-lite";
import { MouseEvent, ReactNode, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { ProviderIcon } from "./ProviderWithLogo/ProviderIcon";
import { SidebarCard } from "./SidebarSearchLayout/Sidebar/Cards";
import { resolveSidebarRequestLogProvider } from "./sidebar-request-log-provider";
import SidebarSignalBubbles from "./SidebarSignalBubbles";
import { BasicTooltip } from "./ui/Tooltip";
import { OverflowDisplayTooltip } from "./ui/Tooltip/OverflowDisplayTooltip";

import { formatShortTimestamp, formatTimestampUTC } from "@/utils/timestamp";
import { useUser } from "@/context/user-context";
import {
  ERROR_TYPE_LABELS,
  RequestErrorType,
  RequestLogStatus,
  STATUS_STYLES,
} from "@/types/request-status";
import { EntityIcon } from "@/components/ui/icons";
import { EntityType } from "@/queries/folder-entities/types";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { REQUESTS_SIDEBAR_URL_NAMESPACE } from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import { applyStructuredSearchStateToSearchParams } from "@/components/StructuredSearch/hooks/useStructuredSearchURLSync";
import { buildSidebarRequestNavigationSearch } from "@/components/SidebarSearchLayout/Sidebar/SecondarySidebar/SearchBar/buildSidebarRequestNavigationSearch";
import { appendNestedKeyEqualsStructuredFilter } from "@/components/SidebarSearchLayout/Sidebar/SecondarySidebar/SearchBar/appendNestedKeyEqualsStructuredFilter";
import {
  ADD_REQUESTS_STRUCTURED_FILTER_EVENT,
  useSidebarTab,
} from "@/components/SidebarSearchLayout/sidebar-tab-context";
import {
  buildSignalStructuredFilterDetail,
  type SidebarSignalBubble,
} from "@/routes/request-logs/search/signal-search-utils";
import { LLMProviderContext } from "@/providers/ProviderAndModelsProvider/provider-store";

export const SidebarRequestLogCard = observer(
  ({
    active,
    logId,
    descriptor,
    tags,
    signals = [],
    startTime,
    starred,
    prompt,
    promptTitle,
    promptLabel = "Prompt: ",
    promptTextSearchMatch = false,
    response,
    responseTitle,
    responseIsFunction,
    type,
    cardinality,
    promptName,
    promptId,
    promptVersionNumber,
    provider,
    providerId,
    customModelName,
    status,
    errorType,
    errorMessage,
    recentlyViewed = false,
    onViewed,
  }: {
    active: boolean;
    logId: number;
    descriptor: string;
    tags: string[];
    signals?: SidebarSignalBubble[];
    startTime: string;
    starred: boolean;
    prompt: ReactNode;
    promptTitle?: string;
    promptLabel?: string;
    promptTextSearchMatch?: boolean;
    response: ReactNode;
    responseTitle?: string;
    responseIsFunction?: boolean;
    type: string | "request";
    cardinality: number | undefined;
    promptName: string | null;
    promptId?: number | null;
    promptVersionNumber?: number | null;
    provider?: string | null;
    providerId?: number | null;
    customModelName?: string | null;
    status?: RequestLogStatus;
    errorType?: RequestErrorType | null;
    errorMessage?: string | null;
    recentlyViewed?: boolean;
    onViewed?: (id: number) => void;
  }) => {
    const location = useLocation();
    const userContext = useUser();
    const sidebarStore = useStructuredSearchStore();
    const sidebarTab = useSidebarTab();
    const providerContext = useContext(LLMProviderContext);
    const iconProvider = resolveSidebarRequestLogProvider({
      provider,
      providerId,
      providers: providerContext?.store.providers,
    });
    // Request-detail navigation copies sidebar filters into the sidebar URL namespace.
    const requestSearchParams = buildSidebarRequestNavigationSearch({
      sidebarStore,
      returnPathname: location.pathname,
      returnSearch: location.search,
    }).replace(/^\?/, "");
    const requestSearchSuffix = requestSearchParams ? `?${requestSearchParams}` : "";

    const timestamp = formatShortTimestamp(startTime);
    const fullTimestamp = formatTimestampUTC(startTime, {
      includeSeconds: true,
      showRelative: false,
    });

    const trueSignals = signals.filter(signal => signal.isTrue);

    const [tagsList, setTagsList] = useState<string[]>(tags || []);

    // TODO (naveed) LOW: remove useEffect and useState completely and we can use the prop directly.
    useEffect(() => {
      setTagsList(tags || []);
    }, [tags]);

    const cardPath =
      type === "group"
        ? `/workspace/${userContext?.activeWorkspaceId}/group/${logId}${requestSearchSuffix}`
        : `/workspace/${userContext?.activeWorkspaceId}/request/${logId}${requestSearchSuffix}`;
    // Prompt links keep sidebar filters only — never table/legacy keys that migrate into Analytics.
    const promptSearchSuffix = (() => {
      const params = applyStructuredSearchStateToSearchParams(
        new URLSearchParams(),
        {
          filters: sidebarStore.filters,
          dateRange: sidebarStore.dateRange,
          q: sidebarStore.q,
          pageIndex: sidebarStore.pageIndex,
          pageSize: sidebarStore.pageSize,
          sorting: sidebarStore.sorting,
        },
        { namespace: REQUESTS_SIDEBAR_URL_NAMESPACE },
      );
      const value = params.toString();
      return value ? `?${value}` : "";
    })();
    const promptHref =
      type !== "group" &&
      promptName &&
      typeof promptId === "number" &&
      typeof promptVersionNumber === "number" &&
      userContext?.activeWorkspaceId
        ? `/workspace/${userContext.activeWorkspaceId}/prompt/${promptId}/version/${promptVersionNumber}${promptSearchSuffix}`
        : null;
    const previewTextClass = "text-gray-700";
    const previewLabelClass = "text-xs font-semibold text-gray-900";
    const metadataTextClass = "text-gray-500";
    const statusLabel = status === "ERROR" ? "Error" : "Warning";
    const errorLabel =
      errorType && errorType in ERROR_TYPE_LABELS ? ERROR_TYPE_LABELS[errorType] : null;
    const statusTooltipContent =
      status && status !== "SUCCESS" ? (
        <div className="max-w-xs">
          <div className="font-medium">{errorLabel || statusLabel}</div>
          {errorMessage ? <div className="mt-1 text-xs text-gray-300">{errorMessage}</div> : null}
        </div>
      ) : null;

    const handleSignalBubbleClick = (
      event: MouseEvent<HTMLButtonElement>,
      signal: SidebarSignalBubble,
    ) => {
      event.preventDefault();
      event.stopPropagation();

      const filterDetail = buildSignalStructuredFilterDetail(
        signal.signalId,
        signal.name,
        signal.isTrue,
      );
      sidebarTab?.setActiveTab("Requests");
      window.dispatchEvent(
        new CustomEvent(ADD_REQUESTS_STRUCTURED_FILTER_EVENT, { detail: filterDetail }),
      );
      appendNestedKeyEqualsStructuredFilter(sidebarStore, filterDetail);
    };

    return (
      <SidebarCard
        active={active}
        type={type}
        id={String(logId)}
        requestLogHoverCue
        requestLogSelectionCue
        requestLogRecentlyViewedCue={recentlyViewed}
        className="duration-300 ease-out animate-in fade-in-0"
        to={cardPath}
        onClick={() => {
          if (type !== "group") {
            onViewed?.(logId);
          }
        }}
      >
        <div className="flex items-center gap-1.5 overflow-hidden">
          {starred ? (
            <StarIconSolid className="h-4 w-auto shrink-0 text-yellow-500" aria-hidden="true" />
          ) : null}
          {iconProvider ? (
            <ProviderIcon provider={iconProvider} className="h-3.5 w-3.5 shrink-0" />
          ) : null}
          <div className="min-w-0 flex-1 truncate font-medium text-gray-600">
            <div content={customModelName || descriptor} className="block truncate">
              {customModelName || descriptor}
            </div>
          </div>
          {(status && status !== "SUCCESS") || promptTextSearchMatch ? (
            <div className="ml-auto flex shrink-0 items-center gap-1">
              {status && status !== "SUCCESS" ? (
                <BasicTooltip content={statusTooltipContent}>
                  <span
                    className="relative flex h-3 w-3 items-center justify-center"
                    aria-label={`${statusLabel} request`}
                  >
                    <span
                      className={`absolute h-3 w-3 rounded-full opacity-10 ${STATUS_STYLES[status].dot}`}
                    />
                    <span
                      className={`relative h-1.5 w-1.5 rounded-full ${STATUS_STYLES[status].dot}`}
                    />
                  </span>
                </BasicTooltip>
              ) : null}
              {promptTextSearchMatch ? (
                <BasicTooltip content="Matches found in prompt or conversation history">
                  <span
                    className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-blue-50 text-blue-600"
                    aria-label="Matches found in prompt or conversation history"
                  >
                    <Search className="h-3 w-3" strokeWidth={2.2} />
                  </span>
                </BasicTooltip>
              ) : null}
            </div>
          ) : null}
        </div>
        <div>
          <div
            className={`line-clamp-2 ${previewTextClass}`}
            title={promptTitle ?? (typeof prompt === "string" ? prompt : undefined)}
          >
            {type !== "group" ? (
              <span className={previewLabelClass}>{promptLabel}</span>
            ) : (
              <span className={previewLabelClass}>Group </span>
            )}
            {type === "group" && <span className="font-normal">{`[${logId}]`}</span>}
            {type !== "group" && <span className="font-normal">{prompt}</span>}
          </div>
          <div
            className={`line-clamp-2 ${previewTextClass} mt-1`}
            title={responseTitle ?? (typeof response === "string" ? response : undefined)}
          >
            <span className={previewLabelClass}>
              {type !== "group" ? (
                <span className={previewLabelClass}>Response: </span>
              ) : (
                <span className={previewLabelClass}>Size: </span>
              )}
            </span>
            {type !== "group" ? (
              <span className={`font-normal ${responseIsFunction && "font-mono"}`}>
                {response}
                {responseIsFunction && "()"}
              </span>
            ) : (
              <span className="font-normal">
                {cardinality} {cardinality === 1 ? "request" : "requests"}
              </span>
            )}
          </div>
        </div>
        {(trueSignals.length > 0 || tagsList.length > 0) && (
          <div className="space-y-1">
            {trueSignals.length > 0 ? (
              <SidebarSignalBubbles
                signals={trueSignals}
                active={active}
                onSignalClick={handleSignalBubbleClick}
              />
            ) : null}
            {tagsList.map((tag: string) => (
              <div
                key={tag}
                className={`mr-1 inline-flex items-center rounded-sm px-1.5 py-0.5 font-medium ${
                  active ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"
                }`}
              >
                {tag}
              </div>
            ))}
          </div>
        )}
        <div className={`flex gap-x-4 ${promptName ? "justify-between" : "justify-end"}`}>
          {promptName === null || !promptName ? null : (
            <div
              className={`group/prompt-footer inline-flex min-w-0 flex-grow items-center gap-1 font-normal ${metadataTextClass}`}
            >
              <EntityIcon type={EntityType.PROMPT} size="xs" className="flex-shrink-0" />
              <OverflowDisplayTooltip content={promptName} className="min-w-0 flex-1 truncate">
                {promptName}
              </OverflowDisplayTooltip>
              {promptHref ? (
                <BasicTooltip content="Open prompt in new tab">
                  <button
                    type="button"
                    aria-label="Open prompt in new tab"
                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 opacity-0 transition-opacity hover:text-blue-600 focus-visible:opacity-100 group-hover/prompt-footer:opacity-100"
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      window.open(promptHref, "_blank", "noopener,noreferrer");
                    }}
                  >
                    <ExternalLinkIcon className="h-3.5 w-3.5" />
                  </button>
                </BasicTooltip>
              ) : null}
            </div>
          )}
          <div
            title={fullTimestamp}
            className={`shrink-0 whitespace-nowrap font-normal ${metadataTextClass}`}
          >
            {timestamp}
          </div>
        </div>
      </SidebarCard>
    );
  },
);
